/**
 * DensityContext — global Cozy vs Compact toggle.
 *
 * Directive §3: Power-user high-density mode. Persisted in localStorage
 * (`md_density`) and mirrored on <html data-density="…"> + the `.density-compact`
 * class so global CSS overrides can key off either.
 *
 * Toggling density dispatches a window resize event so masonry grids
 * (DashboardGrid.jsx) recompute their available columns instantly.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "md_density";
const DEFAULT_MODE = "cozy";
const MODES = /** @type {const} */ (["cozy", "compact"]);

const DensityContext = createContext({
    mode: DEFAULT_MODE,
    isCompact: false,
    setMode: () => {},
    toggle: () => {},
});

function readInitialMode() {
    if (typeof window === "undefined") return DEFAULT_MODE;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return MODES.includes(raw) ? raw : DEFAULT_MODE;
    } catch {
        return DEFAULT_MODE;
    }
}

export function DensityProvider({ children }) {
    const [mode, setModeState] = useState(readInitialMode);

    // Persist + reflect on <html> so global CSS can key off either data-attr
    // or the `.density-compact` class. Also dispatch a resize event so
    // downstream masonry / recharts / grid layouts recompute breakpoints.
    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            /* localStorage may be disabled in private mode; ignore */
        }
        const root = document.documentElement;
        root.setAttribute("data-density", mode);
        root.classList.toggle("density-compact", mode === "compact");
        // Reflow triggers recharts + react-grid-layout + our own DashboardGrid.
        window.dispatchEvent(new Event("resize"));
    }, [mode]);

    const setMode = useCallback((next) => {
        setModeState((prev) => (MODES.includes(next) && next !== prev ? next : prev));
    }, []);

    const toggle = useCallback(() => {
        setModeState((prev) => (prev === "cozy" ? "compact" : "cozy"));
    }, []);

    return (
        <DensityContext.Provider value={{ mode, isCompact: mode === "compact", setMode, toggle }}>
            {children}
        </DensityContext.Provider>
    );
}

export function useDensity() {
    return useContext(DensityContext);
}

export const DENSITY_MODES = MODES;
